import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BaseHttpService } from '../http/base.http.service';
import { ApiRouterService } from '../http/api.router';
import { SingleStopReservationType } from 'src/app/models/enums';
import moment from 'moment';

@Injectable({
  providedIn: 'root',
})
export class TownsHttpService extends BaseHttpService {
  private baseURL = `towns`;

  constructor(
    httpClient: HttpClient,
    private routerService: ApiRouterService,
  ) {
    super(httpClient);
  }

  public getTown(townId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${townId}`);
    return this.read(url);
  }

  public createTown(body: unknown) {
    const url = this.routerService.mount(this.baseURL);
    return this.create(body, url);
  }

  public updateTownConfig(townId: number, body: unknown) {
    const url = this.routerService.mount(`${this.baseURL}/${townId}/config`);
    return this.update(body, url);
  }

  public deleteTown(townId: number) {
    const url = this.routerService.mount(`${this.baseURL}/${townId}`);
    return this.delete(url);
  }

  public getTowns(page: number = 1, size: number = 10) {
    const path = `?page=${page}&size=${size}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getTownStops(townId: number, clientId: number, routeId?: number) {
    let path = `/${townId}/stops?client_id=${clientId}`;
    if (routeId) {
      path += `&exclude_route_id=${routeId}`;
    }
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public filterTowns(page: number = 1, size: number = 10, searchValue?: string) {
    let path = `?page=${page}&size=${size}`;
    path += searchValue ? `&value=${searchValue}` : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getServices(townId: number) {
    const path = `/${townId}/services`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getServicesList(townId: number, page?: number, size?: number) {
    const path = `/${townId}/services/list?page=${page}&size=${size}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getStopsFrom(townId: number, stopId?: number, dateTime?: Date, mergedStops?: boolean, routeId?: number, lat?: number, lng?: number, applyRestrictions?: boolean) {
    const params: string[] = [];
    if (stopId) params.push(`origin_stop_id=${stopId}`);
    if (lat && lng) {
      params.push(`latitude=${lat}`, `longitude=${lng}`);
    } else {
      if (mergedStops === false) params.push(`merge_stops=${mergedStops}`);
      if (dateTime) params.push(`date_time=${moment(dateTime).format('yyyy-MM-DDTHH:mm')}`);
      if (routeId) params.push(`route_id=${routeId}`);
    }
    if (applyRestrictions === false) params.push(`apply_restrictions=${applyRestrictions}`);
    const path = `/${townId}/stops-from${params.length ? `?${params.join('&')}` : ''}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }
  
  public getAvailabilities(townId: number, userId: number, inStopId: number, outStopId: number, seats: number, prmSeats: number, dateTimes: any, isArrivalTime: boolean = false, reservationId?: number, isSingleReservation?: boolean) {
    // [DISABLED] singleStopReservation feature
    // let singleStopReservationType = undefined;
    // if (isSingleReservation) {
    //   singleStopReservationType = inStopId ? SingleStopReservationType.ORIGIN : SingleStopReservationType.DESTINATION;
    // }
    // let path = `/${townId}/closest-availabilities?in_stop_id=${singleStopReservationType === SingleStopReservationType.DESTINATION ? 0 : inStopId}&user_id=${userId}&date_times=${dateTimes}&is_arrival_time=${isArrivalTime}`;
    // path += `&out_stop_id=${singleStopReservationType === SingleStopReservationType.ORIGIN ? 0 : outStopId}`;
    let path = `/${townId}/closest-availabilities?in_stop_id=${inStopId}&user_id=${userId}&date_times=${dateTimes}&arrival_time=${isArrivalTime}`;
    path += `&out_stop_id=${outStopId}`;
    path += `&seats=${seats}`;
    path += `&prm_seats=${prmSeats}`;
    path += `&only_today=true`;
    path += reservationId ? `&reservation_id=${reservationId}` : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getAvailabilitiesByPositions(townId: number, userId: number, inStopId: number | undefined, inLatitude: number, inLongitude: number, inName: string, outStopId: number | undefined, outLatitude: number, outLongitude: number, outName: string, seats: number, prmSeats: number, dateTimes: any, isArrivalTime: boolean = false, reservationId?: number) {
    let path = `/${townId}/closest-availabilities-position?user_id=${userId}&date_times=${dateTimes}&arrival_time=${isArrivalTime}`;
    if (inStopId) path += `&in_stop_id=${inStopId}`;
    path += `&in_latitude=${inLatitude}`;
    path += `&in_longitude=${inLongitude}`;
    path += `&in_name=${encodeURIComponent(inName)}`;
    if (outStopId) path += `&out_stop_id=${outStopId}`;
    path += `&out_latitude=${outLatitude}`;
    path += `&out_longitude=${outLongitude}`;
    path += `&out_name=${encodeURIComponent(outName)}`;
    path += `&seats=${seats}`;
    path += `&prm_seats=${prmSeats}`;
    path += `&only_today=true`;
    path += reservationId ? `&reservation_id=${reservationId}` : '';
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getRoutesWithStop(townId: number, stopId: number, excludeRouteId?: number) {
    const path = `/${townId}/routes/stop?stop_id=${stopId}${excludeRouteId ? `&exclude_route_id=${excludeRouteId}` : ''}`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }

  public getLatestFeatureId(townId: number) {
    const path = `/${townId}/latest-feature-id`;
    const url = this.routerService.mount(`${this.baseURL}${path}`);
    return this.read(url);
  }
}
